import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, ChevronDownIcon, CircleAlertIcon, CheckCircle2Icon, InfoIcon } from 'lucide-react';
import type { Finding, Severity } from '../../types/aus';
interface FindingRowProps {
  finding: Finding;
  expanded: boolean;
  onToggle: () => void;
}
const severityStyles: Record<Severity, {
  icon: typeof InfoIcon;
  color: string;
  label: string;
  chip: string;
}> = {
  success: {
    icon: CheckCircle2Icon,
    color: 'text-emerald-600',
    label: 'Success',
    chip: 'bg-emerald-50 text-emerald-700'
  },
  info: {
    icon: InfoIcon,
    color: 'text-slate-400',
    label: 'Informational',
    chip: 'bg-slate-100 text-slate-600'
  },
  attention: {
    icon: AlertTriangleIcon,
    color: 'text-amber-500',
    label: 'Attention Required',
    chip: 'bg-amber-50 text-amber-700'
  },
  critical: {
    icon: CircleAlertIcon,
    color: 'text-red-500',
    label: 'Critical',
    chip: 'bg-red-50 text-red-700'
  }
};
export function FindingRow({
  finding,
  expanded,
  onToggle
}: FindingRowProps) {
  const style = severityStyles[finding.severity];
  const Icon = style.icon;
  return <li data-id="element-72" data-mp="1q33weazizt5g">
      <button type="button" aria-expanded={expanded} onClick={onToggle} className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-900" data-id="element-73" data-mp="1q2u1c8zmivsw">
        <Icon aria-hidden="true" className={`mt-0.5 h-4 w-4 shrink-0 ${style.color}`} data-id="element-74" data-mp="1v2qasnr3bffu" />
        <div className="min-w-0 flex-1" data-id="element-75" data-mp="28tbvmi03tzgd">
          <div className="flex flex-wrap items-center gap-2" data-id="element-76" data-mp="28sa8hf5tdmia">
            <p className="text-sm font-semibold text-slate-900" data-id="element-77" data-mp="23u0a6vosm5sg">{finding.title}</p>
            <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ${style.chip}`} data-id="element-78" data-mp="2irsppnucau6n">
              {style.label}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-slate-400" data-id="element-79" data-mp="2dtr8z9kdz6yc">
              {finding.category}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500" data-id="element-80" data-mp="2ku1tg9zp6yi9">{finding.description}</p>
        </div>
        <ChevronDownIcon aria-hidden="true" className={`mt-1 h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-81" data-mp="2psmrq3ve3842" />
      </button>

      <AnimatePresence initial={false} data-id="element-82" data-mp="34jvlmp6brfn0">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} data-id="element-83" data-mp="34jlyd9cnzml0">
            <p className="mx-5 mb-4 rounded-xl bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-600" data-id="element-84" data-mp="3ijen4z4mrsqc">
              {finding.detail}
            </p>
          </motion.div>}
      </AnimatePresence>
    </li>;
}